"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "@/components/ui/hover-card";
import ImageWithFallback from "@/components/ui/image-with-fallback";
import type { EnrichedProfile, Profile } from "@/lib/supabase/types";
import { cn, getProfileLink, slice } from "@/lib/utils";
import { MapPin } from "lucide-react";
import Link from "next/link";
import { ProfileCard } from "./profile-card";
import { UserCard } from "./user-card";

interface HoverCardProfileProps {
	profile: EnrichedProfile | null;
	className?: string;
	variant?: "ghost" | "link" | "default";
	onMouseEnter?: () => void;
}

export function HoverCardProfile({
	profile,
	className,
	variant = "default",
	onMouseEnter,
}: HoverCardProfileProps) {
	return (
		<HoverCard openDelay={20}>
			<HoverCardTrigger asChild>
				<div className="inline-block">
					<UserCard
						variant={variant}
						showAvatar={variant === "default"}
						profile={profile as Profile}
						onMouseEnter={onMouseEnter}
					/>
				</div>
			</HoverCardTrigger>
			<HoverCardContent className="p-0 rounded-2xl overflow-hidden">
				<ProfileCard profile={profile} asCard />
			</HoverCardContent>
		</HoverCard>
	);
}

export function HoverCardProfile2(props: HoverCardProfileProps) {
	const { profile, onMouseEnter, className, variant } = props;
	if (!profile) return null;
	return (
		<HoverCard openDelay={0}>
			<HoverCardTrigger asChild onMouseEnter={onMouseEnter}>
				<Link href={getProfileLink(profile.username || profile.id)}>
					<div className={cn("flex flex-row gap-1", className)}>
						{variant !== "link" && (
							<Avatar>
								<AvatarImage src={profile.avatar_url || "/images/logo.png"} />
								<AvatarFallback>ZM</AvatarFallback>
							</Avatar>
						)}
						<Button variant="link" className="p-0 font-semibold text-link">
							@{profile.username}
						</Button>
					</div>
				</Link>
			</HoverCardTrigger>
			<HoverCardContent className="p-2 rounded-3xl overflow-hidden w-44">
				<ProfileInfo profile={profile} />
			</HoverCardContent>
		</HoverCard>
	);
}

export function ProfileInfo({ profile }: { profile?: EnrichedProfile }) {
	if (!profile) return null;
	return (
		<Link
			href={getProfileLink(profile.id)}
			className="w-full flex flex-col items-center gap-2"
		>
			{profile?.cover_image_url && (
				<ImageWithFallback
					src={profile.cover_image_url}
					width={600}
					height={100}
					alt={profile?.name || ""}
					className="w-full h-32 object-cover rounded-2xl"
				/>
			)}
			<div
				className={cn(
					"z-50 bg-transparent w-full flex flex-col items-center justify-end gap-1",
					profile?.cover_image_url ? "mt-[-70px] mb-4" : "py-6",
				)}
			>
				<Avatar className="size-24 border-2">
					<AvatarImage src={profile.avatar_url || "/images/logo.png"} />
					<AvatarFallback>ZM</AvatarFallback>
				</Avatar>
				<div className="z-50 flex flex-col gap-1 items-center justify-between">
					<h1 className="text-base md:text-lg font-bold py-0">
						{slice(profile?.name, 20)}
					</h1>
					{profile.username && (
						<p className="text-sm font-semibold text-primary/80">
							@{profile.username}
						</p>
					)}
					{profile.description && (
						<p className="text-xs text-primary/80">
							{slice(profile.description, 25)}
						</p>
					)}
					{profile.city && (
						<p className="text-xs flex items-center gap-1">
							<MapPin className="size-4" />
							{profile.city?.name}
						</p>
					)}
				</div>
			</div>
		</Link>
	);
}
